import { Box, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material"
import type { FC } from "react"

import { ProductStatusCell } from "@/components/table/ProductStatusCell"
import type { Transfer } from "@/graphql/codegen/graphql"
import { InventoryWorkOrderLocation, type InventoryWorkOrderLocationProps } from "@/screens/Inventory/components"
import { formatNumber } from "@/utils/number"

interface TransferPrintViewTableProps {
  transfer: Transfer
  fallbackDestination?: InventoryWorkOrderLocationProps
}

export const TransferPrintViewTable: FC<TransferPrintViewTableProps> = ({ transfer, fallbackDestination }) => (
  <Table>
    <TableHead>
      <TableRow>
        <TableCell>Ref #</TableCell>
        <TableCell>Product ID</TableCell>
        <TableCell>Description</TableCell>
        <TableCell>Qty to Move</TableCell>
        <TableCell>Current Location</TableCell>
        <TableCell>Destination</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {transfer.products.flatMap(({ product, pallets }) =>
        pallets.map((pallet) => {
          const uomName = product.packageUsWeightUomDetails?.name
          const hasDestination = !!pallet.destinationWarehouseShelving || !!pallet.destinationLocation
          const destination = hasDestination
            ? { warehouseShelving: pallet.destinationWarehouseShelving, location: pallet.destinationLocation }
            : fallbackDestination

          return (
            <TableRow className='align-top' key={`${product.productId}-${pallet.referenceNbr}`}>
              <TableCell>{pallet.referenceNbr}</TableCell>

              <TableCell>
                <ProductStatusCell product={product} />
              </TableCell>

              <TableCell>
                <Box className='flex flex-col gap-y-1'>
                  <Box className='font-bold'>{product.companyProductDescription}</Box>
                  <Box>{product.vendorDescription}</Box>
                  {pallet.lots.map((lot) => (
                    <Box key={lot.transferLotId}>
                      Lot/Batch: {lot.rawInventory?.lotNbr}
                      {lot.rawInventory?.batchNbr ? ` / ${lot.rawInventory.batchNbr}` : ""}
                    </Box>
                  ))}
                </Box>
              </TableCell>

              <TableCell>
                <Box className='flex flex-col gap-y-1 pt-[44px]'>
                  {pallet.lots.map((lot) => (
                    <Box key={lot.transferLotId}>
                      {formatNumber(lot.qtyToMove!, 0, 0)}
                      {uomName ? ` ${uomName}` : ""}
                    </Box>
                  ))}
                </Box>
              </TableCell>

              <TableCell>
                <InventoryWorkOrderLocation
                  warehouseShelving={pallet.sourceWarehouseShelving}
                  location={pallet.sourceLocation}
                />
              </TableCell>

              <TableCell>
                <InventoryWorkOrderLocation {...destination} />
              </TableCell>
            </TableRow>
          )
        })
      )}
    </TableBody>
  </Table>
)
